/**
 * Infrastructure Layer - MedicalReservationRepositoryImpl
 * 診療予約データリポジトリポートの実装
 */

import type {
  MedicalReservationRepository,
  RegisterRequest,
  RegistrationStatus,
} from '../../application/medicalReservation/ports/MedicalReservationRepository';
import type { RegistrationResult } from '../../domain/medicalReservation/RegistrationResult';
import { createSuccessResult, createFailureResult } from '../../domain/medicalReservation/RegistrationResult';
import {
  RegistrationErrorType,
  type RegistrationError,
  type Result,
  ok,
  err,
  createRegistrationError,
} from '../../domain/medicalReservation/errors';
import {
  registerCsvData,
  checkRegistrationStatus,
  type RegisterCsvResponse,
  type StatusCheckResponse,
} from '../api/medicalReservationApi';
import { CSV_TO_DISPLAY_MAPPING } from '../../constants/csvHeaders';

/**
 * CSVヘッダーを正規化する
 * バックエンドAPIが期待するヘッダー名に変換する
 */
const normalizeHeader = (header: string): string => {
  const trimmed = header.trim();
  return CSV_TO_DISPLAY_MAPPING[trimmed] || trimmed;
};

/**
 * APIレスポンスからRegistrationResultに変換する
 */
const convertToRegistrationResult = (
  response: RegisterCsvResponse
): RegistrationResult => {
  if (response.success && response.data) {
    return createSuccessResult(
      {
        successCount: response.data.results?.successCount ?? 0,
        failureCount: response.data.results?.failureCount ?? 0,
        duplicateCount: 0,
        newRecords: 0,
        updatedRecords: 0,
      },
      response.data.registrationId
    );
  }

  return createFailureResult(
    response.error?.message ?? 'データ登録に失敗しました',
    []
  );
};

/**
 * APIレスポンスからRegistrationStatusに変換する
 */
const convertToRegistrationStatus = (
  registrationId: string,
  response: StatusCheckResponse
): RegistrationStatus => {
  if (response.success && response.data) {
    return {
      registrationId,
      status: response.data.status,
      progress: response.data.progress?.percentage,
      message: `${response.data.results?.successCount ?? 0}件処理完了`,
    };
  }

  return {
    registrationId,
    status: 'FAILED',
    message: response.error?.message ?? 'ステータス取得に失敗しました',
  };
};

/**
 * エラータイプを判定する
 */
const determineErrorType = (error: unknown): RegistrationErrorType => {
  const message = error instanceof Error ? error.message : String(error);

  if (message.includes('duplicate') || message.includes('重複')) {
    return RegistrationErrorType.DUPLICATE;
  }
  if (message.includes('validation') || message.includes('バリデーション')) {
    return RegistrationErrorType.VALIDATION_ERROR;
  }
  if (message.includes('auth') || message.includes('認証') || message.includes('401')) {
    return RegistrationErrorType.AUTH_ERROR;
  }
  if (message.includes('network') || message.includes('ネットワーク') || message.includes('ECONNREFUSED')) {
    return RegistrationErrorType.NETWORK_ERROR;
  }

  return RegistrationErrorType.API_ERROR;
};

/**
 * 診療予約データリポジトリの実装
 */
export class MedicalReservationRepositoryImpl
  implements MedicalReservationRepository
{
  /**
   * 診療予約データを登録する
   */
  async register(
    request: RegisterRequest
  ): Promise<Result<RegistrationResult, RegistrationError>> {
    try {
      // ProcessedReservationからCSV行データに変換
      // ヘッダーを正規化（APIが期待する形式に変換）
      const normalizedHeaders = request.headers.map(normalizeHeader);
      const rows = request.processedData.map((reservation) => {
        // rawDataは正規化されたヘッダーをキーとして持つため、
        // 正規化されたヘッダーでアクセスする
        return normalizedHeaders.map((header) => reservation.rawData[header] ?? '');
      });

      // ファイルサイズを推定（概算）
      const estimatedFileSize = JSON.stringify({ headers: normalizedHeaders, rows }).length;

      // APIを呼び出して登録
      const response = await registerCsvData(
        normalizedHeaders,
        rows,
        request.fileName,
        estimatedFileSize
      );

      // レスポンスを変換
      const result = convertToRegistrationResult(response);

      if (result.success) {
        return ok(result);
      } else {
        return err(
          createRegistrationError(
            RegistrationErrorType.API_ERROR,
            result.message
          )
        );
      }
    } catch (error) {
      const errorType = determineErrorType(error);
      const message =
        error instanceof Error
          ? error.message
          : 'データ登録処理に失敗しました';

      return err(
        createRegistrationError(
          errorType,
          message,
          error instanceof Error ? error.stack : undefined
        )
      );
    }
  }

  /**
   * 登録ステータスを確認する
   */
  async checkStatus(
    registrationId: string
  ): Promise<Result<RegistrationStatus, RegistrationError>> {
    try {
      const response = await checkRegistrationStatus(registrationId);
      const status = convertToRegistrationStatus(registrationId, response);

      return ok(status);
    } catch (error) {
      const errorType = determineErrorType(error);
      const message =
        error instanceof Error
          ? error.message
          : 'ステータス確認に失敗しました';

      return err(
        createRegistrationError(
          errorType,
          message,
          error instanceof Error ? error.stack : undefined
        )
      );
    }
  }
}

/**
 * MedicalReservationRepositoryImplのファクトリ関数
 */
export const createMedicalReservationRepository =
  (): MedicalReservationRepository => {
    return new MedicalReservationRepositoryImpl();
  };
